<script>
import { LOADING_KEY, TagType } from '@/util/constants';
import { mapState, mapGetters, mapActions } from 'vuex';
import EzTable from '@/components/ui/Table';
import EzEntityInfo from '@/components/ui/EntityInfo';
import VPrice from '@/components/v3/elements/VPrice';
import EzFilterList from '@/components/ui/FilterList';
import VFilterDropdown from '@/components/v3/patterns/VFilterDropdown';
import VDatePicker from '@/components/v3/patterns/VDatePicker/index';
import EzSelect from '@/components/ui/Select';
import EzInput from '@/components/ui/Input';
import EzButton from '@/components/ui/Button';
import EzSpinner from '@/components/ui/Spinner/EzSpinner';
import EmptyState from '@/views/common/empty-state';
import EzDrawer from '@/components/ui/Drawer';
import VStatsBox from '@/components/v3/elements/VStatsBox';
import VStatsBoxes from '@/components/v3/elements/VStatsBoxes';
import VLabel from '@/components/v3/elements/VLabel';
import VBadge from '@/components/v3/elements/VBadge';
import EzLoadMore from '@/components/ui/LoadMore';
import VTag from '@/components/v3/elements/VTag';
import EzTagsFilter, { defaultTagFilter } from '@/components/ui/Filter/Tags.vue';
import EzTabs from '@/components/ui/Tabs/EzTabs';
import EzTab from '@/components/ui/Tabs/EzTab';
import dayjs from 'dayjs';
import { debounce } from '@/util/utils';
import downloadAttachment from '@/util/downloadAttachment';

const FilterArea = {
  List: 'list',
  Dropdown: 'dropdown',
};

export default {
  components: {
    EzTable,
    EzEntityInfo,
    VPrice,
    EzFilterList,
    VFilterDropdown,
    VDatePicker,
    EzSelect,
    EzInput,
    EzButton,
    EzSpinner,
    EmptyState,
    EzDrawer,
    VLabel,
    VStatsBox,
    VStatsBoxes,
    VBadge,
    EzLoadMore,
    VTag,
    EzTagsFilter,
    EzTabs,
    EzTab,
  },
  props: {
    reportId: {
      type: Number,
      required: false,
    },
    from: {
      required: false,
      default: null,
    },
    to: {
      required: false,
      default: null,
    },
  },
  data() {
    return {
      LOADING_KEY,
      menuMargins: null,
      meta: {},
      FilterArea,
      dropdownFilters: {
        range: {
          from: dayjs().startOf('M').valueOf(),
          to: dayjs().endOf('M').valueOf(),
        },
      },
      listFilters: {
        term: null,
        range: {
          from: dayjs().startOf('M').valueOf(),
          to: dayjs().endOf('M').valueOf(),
        },
        sortBy: 'sales_high_to_low',
      },
      tagFilter: defaultTagFilter(),
      range: {
        start: dayjs().startOf('M').valueOf(),
        end: dayjs().endOf('M').valueOf(),
      },
      defaultRangeName: 'This Month',
      highlightedDates:[],
      secondaryDates:[],
      enabledDates:[],
      selectedMenuItem: {},
      selectedMenuItemDetails: [],
      selectedMenuItemMeta: {},
      selectedMenuItemPriceChanges: [],
      selectedMenuItemPriceChangesMeta: {},
      filtersDirty: false,
      tagHover: false,
      tagPopOver: {
        tags: [],
        top: null,
        left: null,
      },
      stats: {
        cogsPercent: null,
        salesAmount: null,
      },
      drawerLoading: false,
    };
  },
  computed: {
    ...mapState('entities/users', ['context', 'contextId', 'loggedUser']),
    ...mapState('tags', ['allTags']),
    ...mapGetters('loading', ['getLoading']),
    isLoading() {
      return this.getLoading(LOADING_KEY.VENUE_FETCH_MENU_MARGINS);
    },
    filters() {
      return { ...this.listFilters, ...this.dropdownFilters };
    },
    hasFilters() {
      return Object.entries(this.filters).reduce(
        (prevFilter, [, value]) => prevFilter || !!value,
        false,
      );
    },
    isNotAllTime() {
      return !!this.defaultRangeName && this.defaultRangeName !== 'All Time';
    },
    sortOptions() {
      return [
        { id: 'sales_high_to_low', name: 'Sales $ (high to low)' },
        { id: 'sales_low_to_high', name: 'Sales $ (low to high)' },
        { id: 'sales_quantity_high_to_low', name: 'Sales # (high to low)' },
        { id: 'sales_quantity_low_to_high', name: 'Sales # (low to high)' },
        { id: 'margin_high_to_low', name: 'Margin (h,l)' },
        { id: 'margin_low_to_high', name: 'Margin (l,h)' },
      ];
    },
    hasAnyTags() {
      return Array.isArray(this.menuMargins) && this.menuMargins.some(r => this.getRowTags(r).length > 0);
    },
    tableColumns() {
      return ['name', 'warnings', 'tags', 'quantity', 'estCogs', 'actualCogs',  'salesPercentage', 'totalSales'];
    },
  },
  methods: {
    getApiRange() {
      if (!this.isNotAllTime) return {};
      const src = this.listFilters?.range || this.dropdownFilters?.range || this.range;
      const rawFrom = src?.from ?? src?.start;
      const rawTo = src?.to ?? src?.end;
      if (!rawFrom || !rawTo) return {};

      const fromTs = dayjs(rawFrom).format('YYYY-MM-DD');
      const toTs = dayjs(rawTo).format('YYYY-MM-DD');
      if (toTs < fromTs) return {};

      return { from: fromTs, to: toTs };
    },
    getRowTags(row) {
      return Array.isArray(row?.tags) ? row.tags : [];
    },
    ...mapActions('entities/venues', [
      'venueFetchMenuMargins',
      'venueExportMenuMarginsCSV',
      'venueFetchMenuMarginsStats',
      'venueFetchSingleMenuItem',
      'venueFetchSingleMenuItemSales',
      'venueFetchSingleMenuItemPriceChanges',
      'venueMenuMarginsRebuildCogs',
      'venueMenuMarginsEnabledDates',
    ]),
    ...mapActions('tags', ['venueFetchTags', 'fetchTagColors']),
    async fetchMenuMargins(
      query = {},
      loadingKey = LOADING_KEY.VENUE_FETCH_MENU_MARGINS,
    ) {
      try {
        console.log(this.tagFilter)
        const { data } = await this.venueFetchMenuMargins({
          page: this.meta.page || 1,
          limit: 10,
          ...(this.listFilters.term && { term: this.listFilters.term }),
          ...this.getApiRange(),
          ...(this.tagFilter.tags.length > 0 && {
            tagIds: this.tagFilter.tags.map((tag) => tag.id),
            tagMode: this.tagFilter.matchType,
          }),
          ...(this.listFilters.sortBy && { sort: this.listFilters.sortBy }),
          ...query,
          loadingKey,
        });
        if (loadingKey === LOADING_KEY.VENUE_FETCH_MENU_MARGINS) {
          this.menuMargins = data.data;
        } else {
          this.menuMargins = [...this.menuMargins, ...data.data];
        }
        this.meta = data.meta || {};
      } catch (e) {
        console.error('Error fetching menu margins:', e);
        this.menuMargins = [];
        this.meta = {};
      }
    },
    async fetchMenuMarginsStats() {
      try {
        const { data } = await this.venueFetchMenuMarginsStats({
          ...(this.listFilters.term && { term: this.listFilters.term }),
          ...this.getApiRange(),
          ...(this.tagFilter.tags.length > 0 && {
            tagIds: this.tagFilter.tags.map((tag) => tag.id),
            tagMode: this.tagFilter.matchType,
          }),
        });
        this.stats = {
          cogsPercent: data.data.cogsPercent || 0,
          salesAmount: data.data.salesAmount || 0,
        };
      } catch (e) {
        console.error('Error fetching menu margins stats:', e);
        this.stats = {
          cogsPercent: null,
          salesAmount: null,
        };
      }
    },
    async fetchEnabledDates(){
      const { data } = await this.venueMenuMarginsEnabledDates();
      this.highlightedDates = data.data?.stocktakeAndSaleDates;
      this.secondaryDates = data.data?.saleDates;
      this.enabledDates = [...data.data?.stocktakeAndSaleDates, ...data.data?.saleDates]
    },
    async onLoadMoreClick() {
      await this.fetchMenuMargins(
        { page: (this.meta.page || 1) + 1 },
        LOADING_KEY.VENUE_FETCH_MORE_MENU_MARGINS,
      );
    },
    async fetchAndSetMenuItemData(id) {
      const [{ data: menuItemData }, { data: salesData }] = await Promise.all([
        this.venueFetchSingleMenuItem({
          itemId: id,
          ...this.getApiRange(),
        }),
        this.venueFetchSingleMenuItemSales({
          itemId: id,
          ...this.getApiRange(),
          page: 1,
          limit: 10,
        }),
      ]);
      this.selectedMenuItem = menuItemData.data;
      this.selectedMenuItemDetails = salesData.data;
      this.selectedMenuItemMeta = salesData.meta || {};
    },
    async openDrawerAndLoadMenuItem({ id }) {
      try {
        await this.fetchAndSetMenuItemData(id);
        await this.$router.push({
          name: 'venue-reports-menu-margins',
          params: { id },
        });
        if (this.$refs.menuItemDrawer) {
          this.$refs.menuItemDrawer.open();
        } else {
          await this.$nextTick();
          if (this.$refs.menuItemDrawer) this.$refs.menuItemDrawer.open();
        }
      } catch (e) {
        this.resetDrawerRoute();
        console.error(e);
      } finally {
        this.drawerLoading = false;
      }
    },
    async loadDrawerMenuItem({ id }) {
      try {
        this.drawerLoading = true;
        this.selectedMenuItem = {};
        this.selectedMenuItemDetails = [];
        this.selectedMenuItemMeta = {};
        this.selectedMenuItemPriceChanges = [];
        this.selectedMenuItemPriceChangesMeta = {};

        await this.fetchAndSetMenuItemData(id);
        this.drawerLoading = false;
        if (this.$refs.menuItemDrawer) {
          this.$refs.menuItemDrawer.open();
        } else {
          await this.$nextTick();
          if (this.$refs.menuItemDrawer) {
            this.$refs.menuItemDrawer.open();
          }
        }
      } catch (e) {
        this.drawerLoading = false;
        console.error(e);
      }
    },
    async onLoadMoreSalesClick() {
      if (!this.selectedMenuItem?.id) return;
      try {
        const { data } = await this.venueFetchSingleMenuItemSales({
          itemId: this.selectedMenuItem.id,
          ...this.getApiRange(),
          page: (this.selectedMenuItemMeta.page || 1) + 1,
          limit: 10,
          loadingKey: LOADING_KEY.VENUE_FETCH_MORE_SINGLE_MENU_ITEM_SALES,
        });
        this.selectedMenuItemDetails = [...this.selectedMenuItemDetails, ...data.data];
        this.selectedMenuItemMeta = data.meta || {};
      } catch (e) {
        console.error('Error loading more sales:', e);
      }
    },
    async onLoadMorePriceChangesClick() {
      if (!this.selectedMenuItem?.id || !this.selectedMenuItemPriceChangesMeta.nextId) return;
      try {
        const { data } = await this.venueFetchSingleMenuItemPriceChanges({
          itemId: this.selectedMenuItem.id,
          ...this.getApiRange(),
          nextId: this.selectedMenuItemPriceChangesMeta.nextId,
          nextValue: this.selectedMenuItemPriceChangesMeta.nextValue,
          limit: 10,
          loadingKey: LOADING_KEY.VENUE_FETCH_MORE_SINGLE_MENU_ITEM_PRICE_CHANGES,
        });
        this.selectedMenuItemPriceChanges = [...this.selectedMenuItemPriceChanges, ...data.data];
        this.selectedMenuItemPriceChangesMeta = data.meta || {};
      } catch (e) {
        console.error('Error loading more price changes:', e);
      }
    },
    formatDateRange(from, to) {
      if (!from || !to) return this.defaultRangeName;
      const fromDate = dayjs(from).format('DD MMM');
      const toDate = dayjs(to).format('DD MMM');
      return `${fromDate} - ${toDate}`;
    },
    resetDrawerRoute() {
      this.$router.replace({ name: 'venue-reports-menu-margins' });
    },
    updateFilters: debounce(async function deb(area, event) {
      if (area === FilterArea.List) {
        const [filterName, value] = event;
        if (filterName === 'range') {
          const src = value || null;
          const range = this.isNotAllTime && src
            ? {
                from: dayjs(src.start).valueOf(),
                to: dayjs(src.end).valueOf(),
              }
            : null;
          this.listFilters.range = range;
          this.dropdownFilters.range = range;
        } else if (filterName === 'sortBy') {
          this.listFilters.sortBy = value?.id || value;
        } else if (filterName === 'term') {
          this.listFilters.term = value;
        }
      } else if (area === FilterArea.Dropdown) {
        const src = event?.range ?? this.listFilters.range ?? null;
        const range = this.isNotAllTime && src
          ? {
              from: dayjs(src.start ?? src.from).valueOf(),
              to: dayjs(src.end ?? src.to).valueOf(),
            }
          : null;
        if (src) {
          this.listFilters.range = range;
          this.dropdownFilters.range = range;
        }
        this.tagFilter = this.$refs.tagsFilter.getValue();
        this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());
      }

      await this.refreshMenuMargins();
    }, 300),
    async resetFilters(area) {
      if (area === FilterArea.List) {
        this.listFilters = {
          term: null,
          range: {
            from: dayjs().startOf('M').valueOf(),
            to: dayjs().endOf('M').valueOf(),
          },
          sortBy: 'sales_high_to_low',
        };
        this.dropdownFilters.range = {
          from: dayjs().startOf('M').valueOf(),
          to: dayjs().endOf('M').valueOf(),
        };
        this.range = {
          start: dayjs().startOf('M').valueOf(),
          end: dayjs().endOf('M').valueOf(),
        };
        this.defaultRangeName = 'This Month';
      } else {
        this.dropdownFilters = {
          range: {
            from: dayjs().startOf('M').valueOf(),
            to: dayjs().endOf('M').valueOf(),
          },
        };
        this.listFilters = {
          term: null,
          range: {
            from: dayjs().startOf('M').valueOf(),
            to: dayjs().endOf('M').valueOf(),
          },
          sortBy: 'sales_high_to_low',
        };
        this.range = {
          start: dayjs().startOf('M').valueOf(),
          end: dayjs().endOf('M').valueOf(),
        };
        this.defaultRangeName = 'This Month';
        this.tagFilter = defaultTagFilter();
      }

      this.$nextTick(() => this.$refs.filtersGroup.syncLocalFilters());

      await this.refreshMenuMargins();
    },
    async refreshMenuMargins() {
      this.menuMargins = [];
      this.meta = {};
      await this.fetchMenuMargins();
      await this.fetchMenuMarginsStats();
    },
    changeRangeName(rangeName) {
      this.defaultRangeName = rangeName;
    },
    filterClosed() {
      const { tagsFilter } = this.$refs;
      if (tagsFilter) tagsFilter.revert();
    },
    async exportCSV() {
      try {
        const { data, headers  } = await this.venueExportMenuMarginsCSV({
          ...this.getApiRange(),
          ...(this.tagFilter.tags.length > 0 && {
            tagIds: this.tagFilter.tags.map((tag) => tag.id),
            tagMode: this.tagFilter.matchType,
          }),
          ...(this.listFilters.sortBy && { sort: this.listFilters.sortBy }),
        });
        const filename = (headers['content-disposition'] || '').match(/filename="(.+)"/);
        const name = Array.isArray(filename) ? decodeURI(filename[1]) : 'Menu Report.xlsx';
        downloadAttachment(data, name);
      } catch (e) {
        console.error('Error exporting CSV:', e);
      }
    },
    async rebuildCogs() {
      await this.venueMenuMarginsRebuildCogs({loadingKey: LOADING_KEY.VENUE_REBUILD_COGS_MENU_MARGINS});
      await this.refreshMenuMargins();
    },
    onTagDraftChange() {
      this.filtersDirty = true;
    },
    tagsShowHover(id, tags) {
      this.tagHover = true;
      this.tagPopOver.tags = tags;
      const { top, left, bottom } = this.$refs[`tag-${id}`].getBoundingClientRect();
      const topParent = this.$refs.container?.getBoundingClientRect().top || 0;
      const leftParent = this.$refs.container?.getBoundingClientRect().left || 0;
      this.tagPopOver.top = top - topParent + (bottom - top);
      this.tagPopOver.left = left - leftParent;
    },
    tagsRemoveHover() {
      this.tagHover = false;
      this.tagPopOver = {
        tags: [],
        top: null,
        left: null,
      };
    },
    getCogsColorClass(cogsValue, targetValue) {
      if (cogsValue === null || cogsValue === undefined || targetValue === null || targetValue === undefined) {
        return '';
      }

      if (cogsValue < targetValue) {
        return 'cogs-good';
      }
      if (cogsValue > targetValue) {
        return 'cogs-poor';
      }
      return 'cogs-neutral';
    },
    getCogsDifference(cogsValue, targetValue) {
      if (cogsValue === null || cogsValue === undefined || targetValue === null || targetValue === undefined) {
        return null;
      }

      const diff = cogsValue - targetValue;
      return diff.toFixed(2);
    },
    getCogsDifferenceClass(diff) {
      if (diff === null) return '';

      const numDiff = parseFloat(diff);
      if (numDiff < 0) {
        return 'diff-good';
      }
      if (numDiff > 0) {
        return 'diff-poor';
      }
      return 'diff-neutral';
    },
  },
  async created() {
    if (this.from && this.to) {
      this.dropdownFilters = {
        ...this.dropdownFilters,
        range: {
          from: this.from,
          to: this.to,
        },
      };
      this.listFilters = {
        ...this.listFilters,
        range: {
          from: this.from,
          to: this.to,
        },
      };
      this.range = {
        start: this.from,
        end: this.to,
      };
      this.defaultRangeName = `${dayjs(this.range.start).format('DD MMM')} - ${dayjs(
        this.range.end,
      ).format('DD MMM')}`;
    }

    await Promise.all([
      this.fetchMenuMargins(),
      this.fetchMenuMarginsStats(),
      this.venueFetchTags({ query: { type: TagType.Recipe } }),
      this.fetchTagColors(),
      this.fetchEnabledDates()
    ]);
  },
  async mounted() {
    if (this.reportId) {
      await this.$nextTick();
      await this.loadDrawerMenuItem({ id: this.reportId });
    }
  },
  watch: {
    reportId: {
      immediate: false,
      async handler() {
        if (this.reportId) {
          await this.loadDrawerMenuItem({ id: this.reportId });
        }
      },
    },
    stats: {
      deep: true,
      handler(newStats) {
        this.$emit('updateStats', {
          cogsPercent: newStats.cogsPercent,
          salesAmount: newStats.salesAmount,
        });
      },
    },
  },
};
</script>

<template>
  <div class="container" ref="container">
    <div
      class="popover"
      :style="`top: ${tagPopOver.top}px; left: ${tagPopOver.left}px`"
      v-if="tagHover"
    >
      <v-tag
        v-for="tag in tagPopOver.tags"
        :key="tag.id"
        :color="tag.color.colors[0]"
        :backgroundColor="tag.color.colors[1]"
      >
        {{ tag.name }}
      </v-tag>
    </div>
    <div class="actions mt-24">
      <ez-filter-list
        :filters="listFilters"
        @filterUpdated="(...$event) => updateFilters(FilterArea.List, $event)"
        @resetFilter="resetFilters(FilterArea.List)"
        class="mr-16"
      >
        <ez-input
          formKey="filters"
          label="search"
          name="term"
          class="search"
          placeholder="Search for a Menu Item"
        >
          <template #prefix>
            <font-awesome-icon icon="search" />
          </template>
        </ez-input>
        <v-date-picker
          class="calendar ml-16"
          name="range"
          label="Date"
          v-model="range"
          hide-label
          formKey="filters"
          range-mode
          with-predefined-ranges
          :number-of-calendars="2"
          :select-predefined-range="defaultRangeName"
          @rangeNameChange="changeRangeName"
          displayEndOfRange
          :highlighted-dates="highlightedDates"
          :secondary-dates="secondaryDates"
          :enabled-dates="enabledDates"
          highlighted-dates-label="Sales & Inventory"
          secondary-dates-label="Sales"
        />
        <ez-select
          class="select-sort ml-16"
          name="sortBy"
          :value="listFilters.sortBy"
          :selected="listFilters.sortBy"
          :options="sortOptions"
          hide-label
        />
      </ez-filter-list>
      <v-filter-dropdown
        :filters="dropdownFilters"
        @filterUpdated="$event => updateFilters(FilterArea.Dropdown, $event)"
        @resetFilters="resetFilters(FilterArea.Dropdown)"
        @filterClosed="filterClosed"
        ref="filtersGroup"
      >
        <template #firstRow>
          <v-date-picker
            class="calendar"
            name="range"
            label="Date"
            v-model="range"
            formKey="filters"
            range-mode
            with-predefined-ranges
            :number-of-calendars="2"
            :select-predefined-range="defaultRangeName"
            @rangeNameChange="changeRangeName"
            displayEndOfRange
            is-full-width
          />
        </template>
        <template #thirdRow>
          <ez-tags-filter
            class="tags-filter"
            name="menuTags"
            ref="tagsFilter"
            :value="tagFilter"
            :supportsMatchType="true"
            @draftChange="onTagDraftChange"
          />
        </template>
      </v-filter-dropdown>
      <div class="export-actions">
        <ez-button
          v-if="menuMargins && menuMargins.length"
          type="secondary"
          @click="exportCSV"
        >
          Export
        </ez-button>
        <ez-button
          v-if="context.canRebuildCogs"
          @click="rebuildCogs"
          type="primary">
          Refresh COGS
        </ez-button>
      </div>
    </div>

    <div v-if="isLoading" class="spinner-container mt-40">
      <ez-spinner />
    </div>

    <div v-else-if="menuMargins.length === 0" class="text-center mt-40">
      <empty-state class="empty-state-center">
        <template #badge><img src="@/assets/no-product-empty-state.svg" alt="" /></template>
        <template #title>No menu items found</template>
        <!-- eslint-disable-next-line -->
      </empty-state>
    </div>

    <div v-else-if="menuMargins.length > 0" class="mt-24">
      <ez-table
        :data="menuMargins"
        @rowClick="openDrawerAndLoadMenuItem"
        class="menu-margins-table"
        :headers="{
          name: () => 'Recipe',
          tags: () => 'Tags',
          quantity: () => 'Quantity',
          estCogs: () => 'Est. COGS(%)',
          actualCogs: () => 'Actual COGS(%)',
          grossProfit: () => 'Gross Profit',
          salesPercentage: () => '% of Sales',
          totalSales: () => 'Total Sales',
          warnings: () => ''
        }"
        :columns="tableColumns"
        :columnProps="{
          tags: { class: 'medium-cell' },
          quantity: { class: 'small-cell' },
          estCogs: { class: 'price-cell' },
          actualCogs: { class: 'price-cell' },
          grossProfit: { class: 'price-cell' },
          salesPercentage: { class: 'price-cell' },
          totalSales: { class: 'price-cell' },
          warnings: { class: 'small-cell' },
        }"
      >
        <template #cell-name="{ row }">
          <ez-entity-info :imgUrl="row.image">
            <div class="recipe-info">
              <div class="recipe-info__name" :title="row.name">{{ row.name }}</div>
            </div>
          </ez-entity-info>
        </template>
        <template #cell-tags="{ row }">
          <div
            class="tags"
            v-if="getRowTags(row).length"
            @mouseenter="tagsShowHover(row.id, getRowTags(row))"
            @mouseleave="tagsRemoveHover"
            :ref="`tag-${row.id}`"
          >
            <v-tag
              class="mr-8"
              :color="getRowTags(row)[0]?.color?.colors?.[0] || '#000000'"
              :backgroundColor="getRowTags(row)[0]?.color?.colors?.[1] || '#F5F7FC'"
            >{{ getRowTags(row)[0]?.name }}</v-tag>
            <span v-if="getRowTags(row).length > 1">+{{ getRowTags(row).length - 1 }}</span>
          </div>
          <span v-else>-</span>
        </template>
        <template #cell-quantity="{ row }">
          {{ row.salesQuantity }}
        </template>
        <template #cell-estCogs="{ row }">
          <div class="cogs-cell">
            <span
              v-if="row.estCogsPercent !== null && row.estCogsPercent !== undefined"
              :class="getCogsColorClass(row.estCogsPercent, row.targetCogsPercent)"
              class="cogs-percentage"
            >
              {{ row.estCogsPercent }}%
            </span>
            <span v-else class="cogs-percentage">&mdash;</span>

            <span
              v-if="getCogsDifference(row.estCogsPercent, row.targetCogsPercent) !== null"
              class="cogs-difference cogs-difference--grey"
            >
              {{ getCogsDifference(row.estCogsPercent, row.targetCogsPercent) > 0 ? '+' : '' }}{{
                getCogsDifference(row.estCogsPercent, row.targetCogsPercent)
              }}%
            </span>
          </div>
        </template>
        <template #cell-actualCogs="{ row }">
          <div class="cogs-cell">
            <span
              v-if="row.isActual && row.actualCogsPercent !== null && row.actualCogsPercent !== undefined"
              :class="getCogsColorClass(row.actualCogsPercent, row.targetCogsPercent)"
              class="cogs-percentage"
            >
              {{ row.actualCogsPercent }}%
            </span>
            <span v-else class="cogs-percentage">&mdash;</span>

            <span
              v-if="row.isActual && getCogsDifference(row.actualCogsPercent, row.targetCogsPercent) !== null"
              class="cogs-difference cogs-difference--grey"
            >
              {{ getCogsDifference(row.actualCogsPercent, row.targetCogsPercent) > 0 ? '+' : '' }}{{
                getCogsDifference(row.actualCogsPercent, row.targetCogsPercent)
              }}%
            </span>
          </div>
        </template>
        <template #cell-warnings="{ row: { warnings } }">
          <font-awesome-icon
            v-if="warnings.length > 0"
            icon="exclamation-triangle"
            v-tooltip="{
            placement: 'top-start',
            content: warnings.join('<br>'),
            html: true
          }"
          />
        </template>
        <template #cell-grossProfit="{ row }">
          <v-price :price="row.grossProfit"/>
        </template>
        <template #cell-salesPercentage="{ row }">
          {{ row.salesPercentage }}%
        </template>
        <template #cell-totalSales="{ row }">
          <v-price :price="row.salesAmount" />
        </template>
      </ez-table>
      <ez-load-more
        v-if="meta?.page < meta?.lastPage"
        :loading="getLoading(LOADING_KEY.VENUE_FETCH_MORE_MENU_MARGINS)"
        @loadMore="onLoadMoreClick"
      >
        Load More
      </ez-load-more>
    </div>

    <ez-drawer ref="menuItemDrawer" @onClose="resetDrawerRoute">
      <template #title>
        <v-badge>Menu Item</v-badge>
      </template>
      <template #content>
        <div v-if="drawerLoading" class="drawer-loading__container">
          <ez-spinner />
        </div>
        <div v-else class="item-drawer__container">
          <div class="info-section">
            <div>
              <h3>{{ selectedMenuItem?.name }}</h3>
              <div class="price-container" v-if="selectedMenuItem?.salesPrice">
                <v-price
                  :price="selectedMenuItem.salesPrice || 0"
                  flex-row
                />
                <span class="price-label">/ Menu Item</span>
              </div>
            </div>
            <div class="info-section__image">
              <img
                v-if="selectedMenuItem?.image"
                :src="selectedMenuItem?.image"
                alt="Menu Item Image"
                class="menu-item-image"
              />
            </div>
          </div>
          <div v-if="getRowTags(selectedMenuItem).length" class="tags-section mt-16">
            <span class="tags-section__label">Tags</span>
            <div class="tags-section__display">
              <v-tag
                v-for="tag in getRowTags(selectedMenuItem)"
                :key="tag.id"
                :color="tag.color?.colors?.[0] || '#000000'"
                :backgroundColor="tag.color?.colors?.[1] || '#F5F7FC'"
              >
                {{ tag.name }}
              </v-tag>
            </div>
          </div>
          <hr class="ml-24 mr-24"/>
          <v-label as="small" class="stats-label mt-16 mb-8 ml-24 mr-24">
            Stats for {{ formatDateRange(dropdownFilters.range && dropdownFilters.range.from, dropdownFilters.range && dropdownFilters.range.to) }}
          </v-label>
          <v-stats-boxes class="mb-16 ml-24 mr-24 stats-boxes--large">
            <v-stats-box>
              <template #label>Sales</template>
              <template #value>{{ selectedMenuItem.salesAmount | price }}</template>
            </v-stats-box>
            <v-stats-box>
              <template #label>Quantity</template>
              <template #value>{{ selectedMenuItem.salesQuantity | formatNumber }}</template>
            </v-stats-box>
            <v-stats-box>
              <template #label>Gross Profit</template>
              <template #value>{{ selectedMenuItem.grossProfit | price }}</template>
            </v-stats-box>
            <v-stats-box>
              <template #label>% of Sales</template>
              <template #value>{{ selectedMenuItem.salesPercentage }}%</template>
            </v-stats-box>
            <v-stats-box>
              <template #label>COGS (%)</template>
              <template #value>
                <span :class="getCogsColorClass(selectedMenuItem.actualCogsPercent, selectedMenuItem.targetCogsPercent)">
                  {{ selectedMenuItem.actualCogsPercent }}%
                </span>
              </template>
            </v-stats-box>
            <v-stats-box>
              <template #label>COGS Variance</template>
              <template #value>
                <span
                  :class="getCogsDifferenceClass(
                    getCogsDifference(selectedMenuItem.actualCogsPercent, selectedMenuItem.targetCogsPercent)
                  )"
                >
                  {{
                    getCogsDifference(selectedMenuItem.actualCogsPercent, selectedMenuItem.targetCogsPercent) > 0
                      ? '+'
                      : ''
                  }}{{ getCogsDifference(selectedMenuItem.actualCogsPercent, selectedMenuItem.targetCogsPercent) }}%
                </span>
              </template>
            </v-stats-box>
          </v-stats-boxes>

          <div class="drawer-table-container ml-24 mr-24 pb-24">
            <ez-tabs>
              <ez-tab title="Sales History" hash="sales-history">
                <div class="tab-content">
                  <ez-table
                    :data="selectedMenuItemDetails"
                    :headers="{
                      date: () => 'Sales Date',
                      quantity: () => 'Quantity',
                      salesPercentage: () => '% of Sales',
                      cogs: () => 'COGS (%)',
                      total: () => 'Sales',
                    }"
                    :columns="['date','quantity','salesPercentage','cogs', 'total']"
                    :columnProps="{
                      date: { class: 'large-cell' },
                      quantity: { class: 'small-cell' },
                      salesPercentage: { class: 'small-cell' },
                      cogs: { class: 'price-small-cell' },
                      total: { class: 'price-cell' },
                    }"
                  >
                    <template #cell-date="{ row }">
                      <span class="price-gray">{{ row.date }}</span>
                    </template>
                    <template #cell-total="{ row }">
                      <v-price :price="row.total" />
                    </template>
                    <template #cell-quantity="{ row }">
                      {{ row.quantity }}
                    </template>
                    <template #cell-salesPercentage="{ row }">
                      {{ row.salesPercentage }}%
                    </template>
                    <template #cell-cogs="{ row }">
                      <div class="cogs-cell">
                        <span class="cogs-percentage">
                          {{ row.cogsPercent }}%
                        </span>
                        <span
                          class="cogs-difference"
                          :class="{
                            'price-green': row.cogsDiff < 0,
                            'price-red': row.cogsDiff > 0,
                            'price-gray': row.cogsDiff === 0
                          }"
                        >
                          {{ row.cogsDiff > 0 ? '+' : '' }}{{ Number(row.cogsDiff).toFixed(2) }}%
                        </span>
                      </div>
                    </template>
                  </ez-table>
                  <ez-load-more
                    v-if="selectedMenuItemMeta?.page < selectedMenuItemMeta?.lastPage"
                    :loading="getLoading(LOADING_KEY.VENUE_FETCH_MORE_SINGLE_MENU_ITEM_SALES)"
                    @loadMore="onLoadMoreSalesClick"
                    class="mt-16"
                  >
                    Load More
                  </ez-load-more>
                </div>
              </ez-tab>
            </ez-tabs>
          </div>
        </div>
      </template>
    </ez-drawer>

  </div>
</template>

<style scoped lang="scss">
.actions {
  display: flex;
  align-items: flex-start;
  justify-content: left;
  flex-wrap: wrap;
  gap: 16px;
}

.calendar {
  :deep() .date-picker {
    .ez-simple-dropdown__display-container {
      width: 100%;
    }
    .placeholder {
      color: $color-gray-6C;
      min-width: 128px !important;
    }
  }
}

.export-actions {
  position: relative;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.menu-margins-table {
  th, td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid #eceef5;
  }

  th {
    font-weight: 600;
    color: #495057;
  }

  tr {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}

.drawer-header {
  padding: 24px;
  border-bottom: 1px solid #eceef5;
}

.drawer-content {
  padding: 24px;
}

.drawer-loading__container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  padding: 24px;
}

.tab-content {
  padding: 24px 0;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eceef5;
}

.detail-label {
  font-weight: 500;
  color: #6c757d;
}

.detail-value {
  font-weight: 600;
  color: #212529;
}

.sales-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sale-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.sale-date {
  font-weight: 500;
  color: #6c757d;
}

.sale-quantity {
  color: #495057;
}

.sale-amount {
  font-weight: 600;
  color: #212529;
}

.info-section {
  display: flex;
  justify-content: space-between;
  padding: 0 24px 24px;

  h3 {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
  }

  .price-container {
    display: flex;
    align-items: center;
    margin-top: 8px;
  }

  :deep() .v-price--row {
    .v-price__price {
      color: #4d7cfe;
      font-size: 18px;
      letter-spacing: -0.5px;
      line-height: 24px;
      font-weight: 500;
    }
    .v-price__unit--row {
      color: #6c757d;
      font-size: 14px;
      font-weight: 400;
    }
  }

  .price-label {
    color: #6c757d;
    font-size: 14px;
    margin-left: 4px;
  }

  &__image {
    flex-shrink: 0;

    .menu-item-image {
      width: 120px;
      height: 120px;
      object-fit: cover;
      object-position: center;
      border: 1px solid #dee1e4;
      border-radius: 6px;
    }
  }
}

.item-drawer__container {
  .contact-info__item {
    margin-left: 24px;
    margin-right: 24px;
    padding: 16px 0px;
    border-bottom: 1px dashed #dee1e4;

    .label {
      font-size: 12px;
      line-height: 18px;
      margin-bottom: 4px;
    }

    .contact-info__value {
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
    }
  }
}

.stats-label {
  line-height: 18px;
}

.drawer-table-container {
  .table-title {
    font-size: 16px;
    line-height: 22px;
    font-weight: 600;
    letter-spacing: -0.5px;
  }
}

.recipe-history-content {
  .recipe-sections {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .recipe-section {
    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: #e74c3c;
      margin-bottom: 12px;
    }

    .section-content {
      padding-left: 16px;
    }

    .recipe-item {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #eceef5;

      &:last-child {
        border-bottom: none;
      }
    }

    .item-label {
      font-weight: 500;
      color: #6c757d;
    }

    .item-value {
      font-weight: 600;
      color: #212529;
    }
  }
}

.price-green {
  color: #28a745;
}

.price-red {
  color: #dc3545;
}

.price-gray {
  color: #6c757d;
}

.cogs-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.cogs-percentage {
  font-weight: 600;
  line-height: 1.2;
  font-size: 16px;
}

.cogs-difference {
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
}
.cogs-difference--grey { color: #6c757d; }

.cogs-good {
  color: #28a745;
}

.cogs-poor {
  color: #dc3545;
}

.cogs-neutral {
  color: #6c757d;
}

.diff-good {
  color: #28a745;
}

.diff-poor {
  color: #dc3545;
}

.diff-neutral {
  color: #6c757d;
}

.drawer-header__text--ellipses {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tags-section {
  padding: 0 24px;

  &__label {
    display: block;
    font-size: 12px;
    line-height: 18px;
    color: $color-gray-6C;
    margin-bottom: 8px;
    font-weight: 400;
  }

  &__display {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.container {
  position: relative;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
  width: 100%;
}

.popover {
  position: absolute;
  background: white;
  border: 1px solid #e1e5ed;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 300px;
}

.tags {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

:deep() .dropdown-filters {
  .tags-filter {
    .tags-wrapper {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }

    .tags-wrapper__selected {
      padding-bottom: 22px;
    }

    .input-group.search {
      display: flex;
      align-items: center;
      margin: 22px 0;
      font-weight: 600;
    }

    .input-group__label {
      margin-bottom: 0;
      margin-right: 16px;
    }
  }
}

.item-name {
  font-weight: 500;
  color: #212529;
}

:deep() .stats-box__value {
  font-size: 20px;
  line-height: 28px;
}

</style>



